import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { todayInPH, addPhDays } from '@/lib/phDate';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { parseTypedAmount } from '@/lib/number';
import { StockingFields } from '@/components/pond/page/StockingFields';
import { EMPTY_STOCKING_TEXT, parseStockingText, type StockingFieldKey, type StockingFieldText } from '@/lib/stockingFields';

interface CycleStartModalProps {
  open: boolean;
  onClose: () => void;
  farmId: string;
  pondId: string;
  pondName: string;
}

interface CycleFormData {
  stocking_date: string;
  species: string;
  stocking_count: string;
  stocking_density: string;
  pl_stage: string;
  target_harvest_date: string;
  notes: string;
}

const SPECIES_OPTIONS = [
  { value: 'vannamei', label: 'Vannamei (Pacific White)' },
  { value: 'monodon', label: 'Monodon (Black Tiger)' },
  { value: 'other', label: 'Other' },
];

const PL_STAGE_OPTIONS = [
  { value: '', label: 'Select PL stage' },
  { value: 'PL10', label: 'PL10' },
  { value: 'PL12', label: 'PL12' },
  { value: 'PL15', label: 'PL15' },
  { value: 'PL18', label: 'PL18' },
  { value: 'PL20', label: 'PL20' },
];

export function CycleStartModal({
  open,
  onClose,
  farmId,
  pondId,
  pondName,
}: CycleStartModalProps) {
  const queryClient = useQueryClient();
  const today = todayInPH();
  const defaultHarvestDate = addPhDays(today, 100);

  const [formData, setFormData] = useState<CycleFormData>({
    stocking_date: today,
    species: 'vannamei',
    stocking_count: '',
    stocking_density: '',
    pl_stage: '',
    target_harvest_date: defaultHarvestDate,
    notes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CycleFormData, string>>>({});
  const [details, setDetails] = useState<StockingFieldText>(EMPTY_STOCKING_TEXT);

  const createMutation = useMutation({
    mutationFn: async (data: CycleFormData) => {
      const payload: Record<string, unknown> = {
        pond_id: pondId,
        stocking_date: data.stocking_date,
        species: data.species,
      };

      const count = parseTypedAmount(data.stocking_count, { integer: true });
      if ('value' in count && count.value !== null) {
        payload.stocking_count = count.value;
      }
      const density = parseTypedAmount(data.stocking_density, { integer: true });
      if ('value' in density && density.value !== null) {
        payload.stocking_density = density.value;
      }
      if (data.pl_stage) {
        payload.pl_stage = data.pl_stage;
      }
      const extra = parseStockingText(details).payload;
      if (extra.pieces_ordered !== null) payload.pieces_ordered = extra.pieces_ordered;
      if (extra.ordered_allowance_pct !== null) payload.ordered_allowance_pct = extra.ordered_allowance_pct;
      if (Object.keys(extra.stocking_extras).length > 0) payload.stocking_extras = extra.stocking_extras;
      if (data.target_harvest_date) {
        payload.target_harvest_date = data.target_harvest_date;
      }
      if (data.notes.trim()) {
        payload.notes = data.notes.trim();
      }

      return api.post(`/api/v1/farms/${farmId}/cycles`, payload);
    },
    onSuccess: () => {
      toast.success(`Cycle started for ${pondName}!`);
      // Invalidate relevant queries
      // Sweep all pond-scoped caches for this farm so the invalidate hits
      // every shape (detail, siblings, and per-route entry caches).
      queryClient.invalidateQueries({ queryKey: qk.pond.all(farmId) });
      queryClient.invalidateQueries({ queryKey: ['farm', 'overview', farmId] });
      queryClient.invalidateQueries({ queryKey: ['cycles', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
      handleClose();
    },
    onError: (error: Error) => {
      if (error.message.includes('active cycle')) {
        toast.error('This pond already has an active cycle');
      } else {
        toast.error(error.message || 'Failed to start cycle');
      }
    },
  });

  const handleClose = () => {
    setFormData({
      stocking_date: today,
      species: 'vannamei',
      stocking_count: '',
      stocking_density: '',
      pl_stage: '',
      target_harvest_date: defaultHarvestDate,
      notes: '',
    });
    setErrors({});
    setDetails(EMPTY_STOCKING_TEXT);
    onClose();
  };

  const handleChange = (field: keyof CycleFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CycleFormData, string>> = {};

    if (!formData.stocking_date) {
      newErrors.stocking_date = 'Stocking date is required';
    }

    // Whole numbers; "350,000" is 350000 and "3,5" or "abc" is refused with a message.
    const count = parseTypedAmount(formData.stocking_count, { integer: true });
    if ('error' in count) {
      newErrors.stocking_count = count.error;
    }

    const density = parseTypedAmount(formData.stocking_density, { integer: true });
    if ('error' in density) {
      newErrors.stocking_density = density.error;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate() && Object.keys(parseStockingText(details).errors).length === 0) {
      createMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} title={`Start Cycle - ${pondName}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Stocking Date"
          type="date"
          value={formData.stocking_date}
          onChange={(e) => handleChange('stocking_date', e.target.value)}
          error={errors.stocking_date}
        />

        <Select
          label="Species"
          value={formData.species}
          onChange={(e) => handleChange('species', e.target.value)}
          options={SPECIES_OPTIONS}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Stocking Count"
            type="text"
            inputMode="numeric"
            placeholder="e.g., 100000"
            value={formData.stocking_count}
            onChange={(e) => handleChange('stocking_count', e.target.value)}
            error={errors.stocking_count}
            description="Total PLs stocked"
          />

          <Input
            label="Stocking Density"
            type="text"
            inputMode="numeric"
            placeholder="e.g., 50"
            value={formData.stocking_density}
            onChange={(e) => handleChange('stocking_density', e.target.value)}
            error={errors.stocking_density}
            description="PLs per sq. meter"
          />
        </div>

        <Select
          label="PL Stage"
          value={formData.pl_stage}
          onChange={(e) => handleChange('pl_stage', e.target.value)}
          options={PL_STAGE_OPTIONS}
        />

        <StockingFields
          text={details}
          onChange={(key: StockingFieldKey, value: string) => setDetails((t) => ({ ...t, [key]: value }))}
          count={(() => {
            const r = parseTypedAmount(formData.stocking_count, { integer: true });
            return 'value' in r ? r.value : null;
          })()}
          areaM2={null}
          idPrefix="start-cycle"
        />

        <Input
          label="Target Harvest Date"
          type="date"
          value={formData.target_harvest_date}
          onChange={(e) => handleChange('target_harvest_date', e.target.value)}
          description="Estimated harvest date (~100 days)"
        />

        <Textarea
          label="Notes"
          placeholder="Any notes about this cycle..."
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
        />

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMutation.isPending}
          >
            Start Cycle
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
